// Cashflow feature — shared styles extracted from DailyCashflow.tsx
import React from 'react';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';

export const VHEAD: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#64748b',
    overflow: 'hidden',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
};
export const VCELL: React.CSSProperties = {
    padding: '4px 10px',
    fontSize: '0.78rem',
    color: '#1e293b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
};
export const VROW_BASE: React.CSSProperties = {
    display: 'grid',
    alignItems: 'center',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: 'white',
    boxSizing: 'border-box',
};
export const VTOTAL: React.CSSProperties = {
    display: 'grid',
    alignItems: 'center',
    padding: '6px 0',
    borderTop: '2px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    flexShrink: 0,
    fontSize: '0.78rem',
    fontWeight: 700,
};
export const VWRAP: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
};
export const VHEAD_ROW: React.CSSProperties = {
    display: 'grid',
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    flexShrink: 0,
};

// ── Lightweight chip replacements for virtual rows (no MUI Chip overhead) ──
export const CHIP_DEBIT: React.CSSProperties = {
    fontSize: '0.65rem', lineHeight: '18px', padding: '0 6px',
    backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 700,
    borderRadius: 9, display: 'inline-block', whiteSpace: 'nowrap',
};
export const CHIP_CREDIT: React.CSSProperties = {
    fontSize: '0.65rem', lineHeight: '18px', padding: '0 6px',
    backgroundColor: '#dcfce7', color: '#16a34a', fontWeight: 700,
    borderRadius: 9, display: 'inline-block', whiteSpace: 'nowrap',
};
// Plain button — no ripple, no MUI overhead, no mouse-listener from Tooltip
export const ROW_BTN: React.CSSProperties = {
    padding: 3, color: '#94a3b8', background: 'none', border: 'none',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    borderRadius: '50%', lineHeight: 0,
};

// Lightweight selection circle — replaces MUI Checkbox on mobile
export const SELECT_CIRCLE: React.CSSProperties = {
    width: 22, height: 22, borderRadius: '50%',
    border: '2px solid #cbd5e1', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'all 0.15s ease',
};
export const SELECT_CIRCLE_ACTIVE: React.CSSProperties = {
    width: 22, height: 22, borderRadius: '50%',
    border: '2px solid #2563eb', backgroundColor: '#2563eb', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'all 0.15s ease',
};

export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#f8fafc',
        },
        tabs: {
            backgroundColor: 'white',
            borderBottom: '1px solid #e2e8f0',
            minHeight: '40px',
        },
        tab: {
            minHeight: '40px',
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'none',
            minWidth: '100px',
        },
        tabPanel: {
            flex: 1,
            overflowY: 'hidden',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            minHeight: 0,
        },
        controlsRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
        },
        balanceInput: { width: '140px' },
        horizonSelect: { minWidth: '120px' },
        summaryRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
        },
        chartWrap: {
            height: '220px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            padding: '8px',
        },
        tableWrap: {
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
        },
        headCell: {
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#64748b',
            padding: '6px 10px',
            backgroundColor: '#f1f5f9',
        },
        cell: {
            fontSize: '0.78rem',
            padding: '4px 10px',
        },
        atRiskRow: { backgroundColor: '#fff1f2' },
        whatIfRow: { backgroundColor: '#fefce8' },
        creditText: { color: '#16a34a', fontWeight: 600 },
        debitText:  { color: '#dc2626' },
        riskBalance:{ color: '#dc2626', fontWeight: 700 },
        safeBalance:{ fontWeight: 600 },
        safeChip: {
            backgroundColor: '#dcfce7', color: '#15803d',
            fontWeight: 700, fontSize: '0.75rem',
        },
        riskChip: {
            backgroundColor: '#fee2e2', color: '#dc2626',
            fontWeight: 700, fontSize: '0.75rem',
        },
        whatIfChip: {
            backgroundColor: '#fef9c3', color: '#92400e',
            fontWeight: 700, fontSize: '0.75rem',
        },
        whatIfBtn: {
            marginLeft: 'auto',
            textTransform: 'none',
            fontSize: '0.78rem',
            color: '#2563eb',
            borderColor: '#2563eb',
        },
        categoryDot: {
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            marginRight: '6px',
            flexShrink: 0,
        },
        totalRow: {
            backgroundColor: '#f1f5f9',
            fontWeight: 700,
        },
        sectionLabel: {
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: '#94a3b8',
            padding: '10px 10px 4px',
        },
        emptyState: {
            textAlign: 'center',
            padding: '40px 20px',
            color: '#94a3b8',
            fontSize: '0.85rem',
        },
        rowActionBtn: {
            padding: '3px',
            color: '#94a3b8',
            '&:hover': { color: '#1e3a8a' },
        },
        deleteRowBtn: {
            padding: '3px',
            color: '#94a3b8',
            '&:hover': { color: '#ef4444' },
        },
        addRowBtn: {
            marginTop: '8px',
            textTransform: 'none',
            fontSize: '0.78rem',
            color: '#2563eb',
        },
        typeChipDebit: {
            fontSize: '0.65rem',
            height: '18px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            fontWeight: 700,
        },
        typeChipCredit: {
            fontSize: '0.65rem',
            height: '18px',
            backgroundColor: '#dcfce7',
            color: '#16a34a',
            fontWeight: 700,
        },
        completeCheck: {
            padding: '2px',
            color: '#94a3b8',
        },
        sortLabel: {
            fontSize: '0.72rem',
            fontWeight: 700,
            color: '#64748b',
            '&.MuiTableSortLabel-active': { color: '#1e40af' },
            '& .MuiTableSortLabel-icon': { fontSize: '14px !important', opacity: 0.6 },
            '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': { opacity: 1 },
        },
        groupingRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap' as const,
            gap: '6px',
        },
        groupingLabel: {
            fontSize: '0.7rem',
            color: '#94a3b8',
            fontWeight: 600,
            marginRight: '2px',
            flexShrink: 0,
        },
        activeChip: {
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#1e3a8a',
            color: 'white',
            borderRadius: '6px',
            padding: '1px 2px 1px 8px',
            fontSize: '0.72rem',
            fontWeight: 600,
            gap: '1px',
        },
        activeChipNum: {
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.65rem',
            marginRight: '3px',
        },
        activeChipBtn: {
            color: 'rgba(255,255,255,0.65)',
            padding: '2px',
            fontSize: '0.8rem',
            lineHeight: 1,
            '&:hover': { color: 'white' },
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
        },
        inactiveChip: {
            display: 'inline-flex',
            alignItems: 'center',
            border: '1px dashed #cbd5e1',
            borderRadius: '6px',
            padding: '2px 8px',
            fontSize: '0.72rem',
            color: '#64748b',
            cursor: 'pointer',
            '&:hover': { borderColor: '#1e3a8a', color: '#1e3a8a' },
        },
    })
);
